import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import {
  useGetCountriesQuery,
  useGetStatesByCountryQuery,
} from '~/entities/parent/api/get-countries';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/shared/shadcn/ui/form';
import { Input } from '~/shared/shadcn/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shared/shadcn/ui/select';
import type { ParentFormValues } from '~shared/types/schema';

export function Address({ form }: { form: UseFormReturn<ParentFormValues> }) {
  const { control, register } = form;
  const { data: countries } = useGetCountriesQuery();
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [states, setStates] = useState<Array<{ id: string; name: string }>>([]);

  const { data: statesData } = useGetStatesByCountryQuery(selectedCountry);

  useEffect(() => {
    if (statesData) {
      setStates(
        statesData.map((state) => ({ id: state.name, name: state.name })),
      );
    }
  }, [statesData]);

  // Convert ISO2 to country name when form data loads
  useEffect(() => {
    const countryValue = form.getValues('address.country');
    if (countryValue && countryValue.length === 2) {
      // If it's an ISO2 code
      const country = countries?.find((c) => c.iso2 === countryValue);
      if (country) {
        form.setValue('address.country', country.name, {
          shouldValidate: true,
          shouldDirty: false,
        });
        setSelectedCountry(country.name);
      }
    }
  }, [countries, form]);

  return (
    <div className='space-y-4'>
      <h3 className='text-lg font-semibold'>Address</h3>

      <div className='grid grid-cols-2 gap-4'>
        <FormField
          control={control}
          name='address.street'
          render={() => (
            <FormItem>
              <FormLabel>Street</FormLabel>
              <FormControl>
                <Input placeholder='Street' {...register('address.street')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name='address.apartment'
          render={() => (
            <FormItem>
              <FormLabel>Apartment/Unit</FormLabel>
              <FormControl>
                <Input
                  placeholder='Apartment/Unit'
                  {...register('address.apartment')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className='grid grid-cols-2 gap-4'>
        <FormField
          control={control}
          name='address.city'
          render={() => (
            <FormItem>
              <FormLabel>City/Suburb</FormLabel>
              <FormControl>
                <Input
                  placeholder='City/Suburb'
                  {...register('address.city')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name='address.state'
          render={({ field }) => (
            <FormItem>
              <FormLabel>State/Region</FormLabel>
              <Select
                onValueChange={(value) => {
                  if (value) {
                    form.setValue('address.state', value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
                value={field.value || ''}
                disabled={!selectedCountry}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder='Select a State/Region'>
                      {field.value || 'Select a State/Region'}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {states.map((state) => (
                    <SelectItem key={state.id} value={state.name}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className='grid grid-cols-2 gap-4'>
        <FormField
          control={control}
          name='address.zip'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Zip/Postcode</FormLabel>
              <FormControl>
                <Input placeholder='Zip/Postcode' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name='address.country'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Country</FormLabel>
              <Select
                onValueChange={(value) => {
                  if (value) {
                    const country = countries?.find(
                      (c) => c.name === value || c.iso2 === value,
                    );
                    if (country) {
                      setSelectedCountry(country.name);
                      form.setValue('address.country', country.name, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }
                  }
                }}
                value={
                  countries?.find(
                    (c) => c.iso2 === field.value || c.name === field.value,
                  )?.name || field.value
                }
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder='Select a Country'>
                      {countries?.find(
                        (c) => c.iso2 === field.value || c.name === field.value,
                      )?.name ?? 'Select a Country'}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {countries
                    ?.sort((a, b) => {
                      if (a.iso2 === 'US') return -1;
                      if (b.iso2 === 'US') return 1;
                      if (a.name === 'United States') return -1;
                      if (b.name === 'United States') return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map((country) => (
                      <SelectItem key={country._id} value={country.name}>
                        {country.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
